<template>
  <div class="store-info-magic-wrapper">
    <!-- Magical Map Container -->
    <div class="magic-map-container mb-5" v-if="data && data.lat && data.lng">
      <div class="map-overlay-header">
        <i class="far fa-map-marker-alt"></i>
        <span>موقع المتجر الجغرافي</span>
      </div>
      <GmapMap
        class="magic-map"
        :zoom="12"
        :center="{ lat: +data.lat, lng: +data.lng }"
        ref="map"
      >
        <GmapMarker
          :position="{ lat: +data.lat, lng: +data.lng }"
          :draggable="false"
        />
      </GmapMap>
    </div>

    <!-- Section 1: Basic Store & Contact Info -->
    <div class="section-header-box mb-4">
      <h4 class="magic-section-title mb-1">
        <i class="far fa-building ml-2 text-primary"></i>
        بيانات المتجر والمالك
      </h4>
      <p class="text-muted small mb-0">
        المعلومات الأساسية للاتصال وتفاصيل التسجيل الرسمية
      </p>
    </div>

    <div class="magic-stats-grid mb-5">
      <!-- الاسم التجاري -->
      <div class="magic-stat-card">
        <div class="stat-icon-wrapper purple-gradient">
          <i class="far fa-store"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">الاسم التجاري</span>
          <h4 class="stat-value">{{ data.store?.trad_name || "---" }}</h4>
        </div>
      </div>

      <!-- المالك -->
      <div class="magic-stat-card">
        <div class="stat-icon-wrapper blue-gradient">
          <i class="far fa-user-tie"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">المالك</span>
          <h4 class="stat-value">{{ data.full_name || "---" }}</h4>
        </div>
      </div>

      <!-- الهاتف -->
      <div class="magic-stat-card" v-if="data.phone_complete_form">
        <div class="stat-icon-wrapper green-gradient">
          <i class="far fa-phone-alt"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">الهاتف</span>
          <h4 class="stat-value" dir="ltr">
            {{ data.phone_complete_form }}
          </h4>
        </div>
      </div>

      <!-- البريد الالكتروني -->
      <div class="magic-stat-card" v-if="data.email">
        <div class="stat-icon-wrapper orange-gradient">
          <i class="far fa-envelope"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">البريد الالكتروني</span>
          <h4 class="stat-value">{{ data.email }}</h4>
        </div>
      </div>

      <!-- الدولة -->
      <div class="magic-stat-card" v-if="data.country">
        <div class="stat-icon-wrapper cyan-gradient">
          <i class="far fa-globe"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">الدولة</span>
          <h4 class="stat-value">{{ data.country.name }}</h4>
        </div>
      </div>

      <!-- المدينة -->
      <div class="magic-stat-card" v-if="data.city">
        <div class="stat-icon-wrapper pink-gradient">
          <i class="far fa-city"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">المدينة</span>
          <h4 class="stat-value">{{ data.city.name }}</h4>
        </div>
      </div>

      <!-- الحي الرئيسي -->
      <div class="magic-stat-card" v-if="data.store?.district || data.district">
        <div class="stat-icon-wrapper red-gradient">
          <i class="far fa-map-signs"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">الحي الأساسي</span>
          <h4 class="stat-value">
            {{ (data.store?.district || data.district)?.name }}
          </h4>
        </div>
      </div>

      <!-- الأحياء المغطاة -->
      <div
        class="magic-stat-card col-12"
        v-if="
          (data.districts && data.districts.length) ||
          (data.store?.districts && data.store?.districts.length)
        "
      >
        <div class="stat-icon-wrapper purple-gradient">
          <i class="far fa-draw-polygon"></i>
        </div>
        <div class="stat-content" style="flex: 1">
          <span
            class="stat-label d-flex align-items-center justify-content-between"
          >
            <span>{{ $t("forms.labels.districts") }} (نطاق التغطية)</span>
            <span class="badge bg-purple text-white">
              {{ (data.districts || data.store?.districts || []).length }}
            </span>
          </span>
          <div class="d-flex flex-wrap gap-2 mt-2">
            <span
              v-for="dist in data.districts || data.store?.districts || []"
              :key="dist.id"
              class="badge bg-light text-dark border px-2 py-1"
              style="font-size: 12.5px; border-radius: 6px"
            >
              <i class="fas fa-map-marker-alt text-danger me-1"></i>
              {{ dist.name || (dist.ar ? dist.ar.name : dist.name) }}
            </span>
          </div>
        </div>
      </div>

      <!-- السجل التجاري -->
      <div class="magic-stat-card">
        <div class="stat-icon-wrapper indigo-gradient">
          <i class="far fa-file-certificate"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">السجل التجاري</span>
          <h4 class="stat-value">
            {{ data.store?.commercial_registration_number || "---" }}
          </h4>
        </div>
      </div>

      <!-- هاتف صاحب المطعم -->
      <div
        class="magic-stat-card"
        v-if="data.store?.owner_phone || data.owner_phone"
      >
        <div class="stat-icon-wrapper blue-gradient">
          <i class="far fa-phone-alt"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">هاتف صاحب المطعم</span>
          <h4 class="stat-value" dir="ltr">
            {{ data.store?.owner_phone || data.owner_phone }}
          </h4>
        </div>
      </div>

      <!-- هاتف المحاسب -->
      <div class="magic-stat-card">
        <div class="stat-icon-wrapper teal-gradient">
          <i class="far fa-user-headset"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">هاتف المحاسب</span>
          <h4 class="stat-value" dir="ltr">
            {{ data.store?.accountant_phone || "---" }}
          </h4>
        </div>
      </div>

      <!-- رقم الواتساب -->
      <div
        class="magic-stat-card"
        v-if="data.store?.whatsapp_phone || data.whatsapp_phone"
      >
        <div class="stat-icon-wrapper green-gradient">
          <i class="fab fa-whatsapp"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">رقم الواتساب</span>
          <h4 class="stat-value" dir="ltr">
            <a
              :href="`https://wa.me/${(
                data.store?.whatsapp_phone || data.whatsapp_phone
              ).replace(/[^0-9]/g, '')}`"
              target="_blank"
              class="text-decoration-none text-success"
            >
              {{ data.store?.whatsapp_phone || data.whatsapp_phone }}
            </a>
          </h4>
        </div>
      </div>

      <!-- تصنيف المتجر -->
      <div class="magic-stat-card" v-if="data.store?.place_category">
        <div class="stat-icon-wrapper blue-gradient">
          <i class="far fa-tags"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">تصنيف المتجر</span>
          <h4 class="stat-value">{{ data.store.place_category.name }}</h4>
        </div>
      </div>

      <!-- طريقة تسوية المشتريات -->
      <div class="magic-stat-card">
        <div class="stat-icon-wrapper green-gradient">
          <i class="far fa-hand-holding-usd"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">{{
            $t("forms.labels.purchase_settlement_method")
          }}</span>
          <h4 class="stat-value">
            {{
              (data.store?.purchase_settlement_method ||
                data.purchase_settlement_method) === "CASH_ON_PICKUP"
                ? $t("forms.labels.purchase_settlement_cash_on_pickup")
                : $t("forms.labels.purchase_settlement_credit")
            }}
          </h4>
        </div>
      </div>

      <!-- تصنيف المطعم -->
      <div class="magic-stat-card" v-if="data.store?.restaurant_specialty">
        <div class="stat-icon-wrapper orange-gradient">
          <i class="far fa-utensils"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">تصنيف المطعم</span>
          <h4 class="stat-value">{{ data.store.restaurant_specialty.name }}</h4>
        </div>
      </div>
    </div>

    <!-- Section 2: Activity & Wallet Financial Statistics -->
    <div class="section-header-box mb-4">
      <h4 class="magic-section-title mb-1">
        <i class="far fa-chart-line-up ml-2 text-primary"></i>
        إحصائيات النشاط والماليات
      </h4>
      <p class="text-muted small mb-0">
        تحليلات الأداء، الدخول، ومستحقات المحفظة
      </p>
    </div>

    <div class="magic-stats-grid">
      <!-- حالة الاتصال -->
      <div class="magic-stat-card">
        <div
          class="stat-icon-wrapper"
          :class="data.is_online ? 'green-gradient' : 'red-gradient'"
        >
          <i
            class="far"
            :class="data.is_online ? 'fa-wifi' : 'fa-wifi-slash'"
          ></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">حالة الاتصال</span>
          <h4
            class="stat-value"
            :class="data.is_online ? 'text-success' : 'text-danger'"
          >
            {{ data.is_online ? "متصل الآن" : "غير متصل" }}
          </h4>
        </div>
      </div>

      <!-- اخر تسجيل دخول -->
      <div class="magic-stat-card">
        <div class="stat-icon-wrapper purple-gradient">
          <i class="far fa-clock"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">اخر تسجيل دخول</span>
          <h4 class="stat-value" dir="ltr">
            {{ data.last_login_at || "لم يتم" }}
          </h4>
        </div>
      </div>

      <!-- عدد مرات تسجيل الدخول -->
      <div class="magic-stat-card">
        <div class="stat-icon-wrapper cyan-gradient">
          <i class="far fa-sign-in-alt"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">عدد مرات تسجيل الدخول</span>
          <h4 class="stat-value">{{ data.login_count || 0 }}</h4>
        </div>
      </div>

      <!-- متوسط تقيمات المتجر -->
      <div class="magic-stat-card" v-if="data.wallet_statics">
        <div class="stat-icon-wrapper yellow-gradient">
          <i class="far fa-star"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">متوسط تقيمات المتجر</span>
          <h4 class="stat-value text-warning">
            {{ data.wallet_statics.rate_avg || 0 }} ★
          </h4>
        </div>
      </div>

      <!-- اجمالي التوصيل المجاني -->
      <div class="magic-stat-card" v-if="data.wallet_statics">
        <div class="stat-icon-wrapper pink-gradient">
          <i class="far fa-gift"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">اجمالي التوصيل المجاني</span>
          <h4 class="stat-value">
            {{ data.wallet_statics.free_offers_total || 0 }}
          </h4>
        </div>
      </div>

      <!-- اجمالي خصومات الشركة -->
      <div class="magic-stat-card" v-if="data.wallet_statics">
        <div class="stat-icon-wrapper red-gradient">
          <i class="far fa-badge-percent"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">اجمالي خصومات الشركة</span>
          <h4 class="stat-value">{{ data.wallet_statics.decrements || 0 }}</h4>
        </div>
      </div>

      <!-- قيمة الطلبات المستحقة من المتجر -->
      <div class="magic-stat-card" v-if="data.wallet_statics">
        <div class="stat-icon-wrapper indigo-gradient">
          <i class="far fa-money-bill-wave"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">الطلبات المستحقة من المتجر</span>
          <h4 class="stat-value text-primary font-weight-bold">
            {{ Number(data.wallet_statics.money_needed || 0).toFixed(2) }}
          </h4>
        </div>
      </div>

      <!-- اجمالي مستحقات الشركة -->
      <div class="magic-stat-card" v-if="data.wallet_statics">
        <div class="stat-icon-wrapper teal-gradient">
          <i class="far fa-building"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">اجمالي مستحقات الشركة</span>
          <h4 class="stat-value text-emerald font-weight-bold">
            {{ Number(data.wallet_statics.app_amount_store || 0).toFixed(2) }}
          </h4>
        </div>
      </div>

      <!-- اجمالي قيمة التوصيل -->
      <div class="magic-stat-card" v-if="data.wallet_statics">
        <div class="stat-icon-wrapper blue-gradient">
          <i class="far fa-motorcycle"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">اجمالي قيمة التوصيل</span>
          <h4 class="stat-value">
            {{
              Number(data.wallet_statics.orders_delivery_total || 0).toFixed(2)
            }}
          </h4>
        </div>
      </div>

      <!-- اجمالي الطلبات -->
      <div class="magic-stat-card" v-if="data.wallet_statics">
        <div class="stat-icon-wrapper orange-gradient">
          <i class="far fa-shopping-bag"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">اجمالي الطلبات</span>
          <h4 class="stat-value">
            {{ Number(data.wallet_statics.orders_total || 0).toFixed(2) }}
          </h4>
        </div>
      </div>

      <!-- اجمالي المنتجات -->
      <div class="magic-stat-card" v-if="data.wallet_statics">
        <div class="stat-icon-wrapper purple-gradient">
          <i class="far fa-box-open"></i>
        </div>
        <div class="stat-content">
          <span class="stat-label">اجمالي المنتجات</span>
          <h4 class="stat-value">
            {{ Number(data.wallet_statics.orders_sub_total || 0).toFixed(2) }}
          </h4>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ["data"],
};
</script>

<style lang="scss" scoped>
.store-info-magic-wrapper {
  padding: 0.5rem 0;
  animation: fadeIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Magic Section Title */
.section-header-box {
  .magic-section-title {
    color: #1e293b;
    font-weight: 800;
    display: flex;
    align-items: center;
    font-size: 1.25rem;
  }
}

/* Magic Map Container */
.magic-map-container {
  position: relative;
  height: 340px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 35px rgba(0, 0, 0, 0.08);
  }

  .map-overlay-header {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(12px);
    padding: 8px 18px;
    border-radius: 30px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    font-size: 13px;
    color: var(--mainColor-2, #783d73);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);

    i {
      font-size: 14px;
      color: var(--mainColor-2, #783d73);
    }
  }

  .magic-map {
    width: 100%;
    height: 100%;
  }
}

/* Magic Stats Grid */
.magic-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.magic-stat-card {
  background: #ffffff;
  border-radius: 18px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.03);
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent,
      rgba(255, 255, 255, 0.6),
      transparent
    );
    transform: translateX(-100%);
    transition: 0.6s;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.06);

    &::after {
      transform: translateX(100%);
    }

    .stat-icon-wrapper {
      transform: scale(1.1) rotate(5deg);
    }
  }

  .stat-icon-wrapper {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.4rem;
    color: #ffffff;
    transition: transform 0.3s ease;
    flex-shrink: 0;

    &.blue-gradient {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
      box-shadow: 0 6px 16px rgba(79, 172, 254, 0.25);
    }
    &.purple-gradient {
      background: linear-gradient(
        135deg,
        var(--mainColor-2, #783d73) 0%,
        #9b4f95 100%
      );
      box-shadow: 0 6px 16px rgba(120, 61, 115, 0.25);
    }
    &.orange-gradient {
      background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
      box-shadow: 0 6px 16px rgba(250, 112, 154, 0.25);
    }
    &.green-gradient {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      box-shadow: 0 6px 16px rgba(16, 185, 129, 0.25);
    }
    &.cyan-gradient {
      background: linear-gradient(135deg, #00c6fb 0%, #005bea 100%);
      box-shadow: 0 6px 16px rgba(0, 198, 251, 0.25);
    }
    &.pink-gradient {
      background: linear-gradient(135deg, #ff0844 0%, #ffb199 100%);
      box-shadow: 0 6px 16px rgba(255, 8, 68, 0.25);
    }
    &.red-gradient {
      background: linear-gradient(135deg, #f83600 0%, #f9d423 100%);
      box-shadow: 0 6px 16px rgba(248, 54, 0, 0.25);
    }
    &.indigo-gradient {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      box-shadow: 0 6px 16px rgba(102, 126, 234, 0.25);
    }
    &.teal-gradient {
      background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
      box-shadow: 0 6px 16px rgba(13, 148, 136, 0.25);
    }
    &.yellow-gradient {
      background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
      box-shadow: 0 6px 16px rgba(245, 158, 11, 0.25);
    }
  }

  .stat-content {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .stat-label {
      font-size: 12px;
      color: #94a3b8;
      margin-bottom: 3px;
      font-weight: 600;
    }

    .stat-value {
      font-size: 15px;
      font-weight: 700;
      color: #1e293b;
      margin: 0;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;

      &.text-emerald {
        color: #10b981;
      }
    }
  }
}
</style>
